import { Cesium } from 'app/cesium';
import { LocationLookup } from 'app/shared/selection/locations/LocationLookup';
import {
    Appearance,
    SelectableHeatMapFeature,
} from 'app/map/services/heatmap/types/SelectableHeatMapFeature';
import { IMSVisualiser } from 'app/views/visualiser';
import { AbstractGeoDataMap } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { MeanAggregator } from 'app/views/aggregators/mean_aggregator';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { DataWithLocationArray } from 'app/shared/collections/time/ims_time_collection.factory';

interface Point extends SelectableHeatMapFeature {}

interface PointMap {
    pointList: Point[];
    locationLookup: LocationLookup<Point>;
}

export function createPrimitivePointMap(
    id: number,
    dataByLocation: DataWithLocationArray,
    imsVisualiser: IMSVisualiser,
): AbstractGeoDataMap {
    return new PrimitivePointMap(id, dataByLocation, imsVisualiser);
}

class PrimitivePointMap extends AbstractGeoDataMap {
    private _locationLookup: LocationLookup<Point>;
    private _pointList: Point[];
    private _deleteFunc: () => void;
    private _statType: string;

    constructor(id: number, dataByLocation: DataWithLocationArray, imsVisualiser: IMSVisualiser) {
        super(id, dataByLocation);

        // Always use a mean aggregator for this one since we expect to process stats
        // one at a time since that is all that is returned from the API
        const appearanceGenerator = buildAppearanceWithAggregatorAndVisualiser(
            new MeanAggregator(),
            imsVisualiser,
        );
        const { locationLookup, pointList } = dataByLocationToPointMap(
            dataByLocation,
            appearanceGenerator,
        );
        this._locationLookup = locationLookup;

        const collection = imsVisualiser.valueGetter.statCollection;
        this._statType = collection.getImsType();

        this._pointList = pointList;
    }

    get statType(): string {
        return this._statType;
    }

    delete(): void {
        if (this._deleteFunc) {
            this._deleteFunc();
            this._deleteFunc = null;
        }
    }

    lookupSelectableFeatures(
        latitude: number,
        longitude: number,
        maxResults?: number,
    ): SelectableHeatMapFeature[] {
        return this._locationLookup.lookup(latitude, longitude, maxResults, 10);
    }

    drawOnViewer(viewer: Cesium.Viewer) {
        // Delete it if it already exists
        this.delete();
        // This actually draws the thing
        this._deleteFunc = cesiumPointMap(this.id, viewer, this._pointList);
    }

    getSelectableFeature(featureId: number): SelectableHeatMapFeature {
        // Index is equal to ID
        if (featureId < 0 || featureId >= this._pointList.length) {
            throw new Error(`Invalid point ID: ${featureId}`);
        }
        return this._pointList[featureId];
    }
}

// Responsible for turning data by location into a point map
function dataByLocationToPointMap(
    datums: DataWithLocationArray,
    appearanceGenerator: AppearanceGenerator,
): PointMap {
    const statByPointId = [];
    const pointList = datums.map((d, index) => {
        statByPointId.push([d]);
        return {
            id: index,
            // Used for selection service.
            latitude: d.lat,
            longitude: d.lon,
            appearance: appearanceGenerator([d]),
            datums: [d],
            featureDataList: [],
            featureRadiusMetres: 20, // arbitrary value
        };
    });

    const locationLookup = new LocationLookup<Point>(pointList);

    return {
        pointList,
        locationLookup,
    };
}

/**
 * Creates a single primitive for selection.
 * @param {number} id
 * @param {Cesium.Viewer} viewer
 * @param {Point[]} points
 * @param {number} rectRadius
 * @returns {() => void} The delete primitive function - used for removing from map
 */
function cesiumPointMap(
    id: number,
    viewer: Cesium.Viewer,
    points: Point[],
    rectRadius?: number,
): () => void {
    const primitives = viewer.scene.primitives;
    // Build all the instances for primitive batching.
    const geometryInstances = [];
    points.forEach((point) => geometryInstances.push(pointToGeometryInstance(point, rectRadius)));

    // Add the primitive to the scene
    const primitive = primitives.add(
        new Cesium.GroundPrimitive({
            geometryInstances,
            appearance: new Cesium.PerInstanceColorAppearance(),
            vertexCacheOptimize: true,
            allowPicking: false,
        }),
    );

    // Add our fake entity and lookup function to help with selects
    primitive.entity = {
        getId: () => id,

        selectionType: SelectionType.HEAT_MAP,
    };

    // Destroy the primitive
    return function () {
        primitives.remove(primitive);
    };
}

function pointToGeometryInstance(point: Point, rectRadius: number): Cesium.GeometryInstance {
    return new Cesium.GeometryInstance({
        geometry: buildRectangle(point, rectRadius),
        attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(point.appearance.color),
        },
    } as any);
}

/**
 *
 * @param point Center point of rectangle
 * @param {number} rectRadius 'radius of rectangle'
 * @returns {Cesium.RectangleGeometry}
 */
function buildRectangle(point, rectRadius: number = 0.000061): Cesium.RectangleGeometry {
    // TODO improve and make this in meters
    const west = point.longitude - rectRadius;
    const east = point.longitude + rectRadius;
    const south = point.latitude - rectRadius;
    const north = point.latitude + rectRadius;

    const rect = Cesium.Rectangle.fromDegrees(west, south, east, north);

    return new Cesium.RectangleGeometry({
        rectangle: rect,
        vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT,
    });
}

type AppearanceGenerator = (stats: any[]) => Appearance;

function buildAppearanceWithAggregatorAndVisualiser(
    imsAggregator: IMSAggregator,
    imsVisualiser: IMSVisualiser,
): AppearanceGenerator {
    return function (stats: any[]): Appearance {
        const aggValue = imsAggregator.getAggValueForStatArray(stats, imsVisualiser.valueGetter);
        const color = imsVisualiser.colorRenderer.getColor(aggValue);
        return {
            color,
        };
    };
}
